// Product style + pool style — ported from the approved mockup src/lib/productStyle.ts (§0).
// Each product GROUP (family) keeps its emoji + soft colour so products are recognisable at a glance.

const GROUP_STYLE = {
  "Thigh Fillet Bone Count": { emoji: "🍗", chip: "bg-amber-100 text-amber-800 border-amber-300", dot: "bg-amber-400" },
  "FOT Bone Count": { emoji: "🍖", chip: "bg-rose-100 text-rose-800 border-rose-300", dot: "bg-rose-400" },
  "Drum Fillets": { emoji: "🥁", chip: "bg-violet-100 text-violet-800 border-violet-300", dot: "bg-violet-400" },
  "FOM Bone Count": { emoji: "🦴", chip: "bg-sky-100 text-sky-800 border-sky-300", dot: "bg-sky-400" },
  "GYG Boner": { emoji: "🌮", chip: "bg-lime-100 text-lime-800 border-lime-300", dot: "bg-lime-500" },
  "Cutlets": { emoji: "🥩", chip: "bg-red-100 text-red-800 border-red-300", dot: "bg-red-400" },
  "Wing Nibble": { emoji: "🪽", chip: "bg-cyan-100 text-cyan-800 border-cyan-300", dot: "bg-cyan-400" },
  "Trimming": { emoji: "✂️", chip: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300", dot: "bg-fuchsia-400" },
  "Manual Debone": { emoji: "👐", chip: "bg-emerald-100 text-emerald-800 border-emerald-300", dot: "bg-emerald-400" },
  "CHOP Trim": { emoji: "🥣", chip: "bg-orange-100 text-orange-800 border-orange-300", dot: "bg-orange-400" },
  "Barrels": { emoji: "🛢️", chip: "bg-stone-200 text-stone-700 border-stone-300", dot: "bg-stone-400" },
  "Whole Birds": { emoji: "🐔", chip: "bg-yellow-100 text-yellow-800 border-yellow-300", dot: "bg-yellow-400" },
  "T/Loin": { emoji: "🍱", chip: "bg-pink-100 text-pink-800 border-pink-300", dot: "bg-pink-400" },
  "9-Cut": { emoji: "9️⃣", chip: "bg-indigo-100 text-indigo-800 border-indigo-300", dot: "bg-indigo-400" },
  "FOD": { emoji: "🥁", chip: "bg-violet-100 text-violet-800 border-violet-300", dot: "bg-violet-400" },
};

const KEYWORD_EMOJI = [
  [/wing/i, "🪽"], [/fot/i, "🍖"], [/fom/i, "🦴"], [/thigh/i, "🍗"], [/drum|fod/i, "🥁"],
  [/whole|bird/i, "🐔"], [/cutlet/i, "🥩"], [/trim/i, "✂️"], [/debon/i, "👐"], [/barrel/i, "🛢️"],
  [/loin/i, "🍱"], [/9.?cut/i, "9️⃣"], [/gyg/i, "🌮"],
];
const FALLBACK_CHIP = [
  { chip: "bg-amber-100 text-amber-800 border-amber-300", dot: "bg-amber-400" },
  { chip: "bg-teal-100 text-teal-800 border-teal-300", dot: "bg-teal-400" },
  { chip: "bg-rose-100 text-rose-800 border-rose-300", dot: "bg-rose-400" },
  { chip: "bg-sky-100 text-sky-800 border-sky-300", dot: "bg-sky-400" },
  { chip: "bg-violet-100 text-violet-800 border-violet-300", dot: "bg-violet-400" },
  { chip: "bg-lime-100 text-lime-800 border-lime-300", dot: "bg-lime-500" },
];
const FALLBACK_EMOJI = ["🐣", "🐥", "🧺", "📦", "🍽️", "🧊"];

// Accepts a product object ({family, code}) or a bare code string.
export function productStyle(p) {
  const family = typeof p === "object" ? p?.family : null;
  const code = (typeof p === "object" ? p?.code : p) || "";
  if (!code && !family) return { emoji: "❔", chip: "bg-slate-100 text-slate-600 border-slate-300", dot: "bg-slate-300" };
  const kw = KEYWORD_EMOJI.find(([re]) => re.test(code));
  if (family && GROUP_STYLE[family]) {
    const base = GROUP_STYLE[family];
    return { ...base, emoji: kw?.[1] ?? base.emoji };
  }
  let h = 0;
  for (const c of code) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const fb = FALLBACK_CHIP[h % FALLBACK_CHIP.length];
  return { chip: fb.chip, dot: fb.dot, emoji: kw?.[1] ?? FALLBACK_EMOJI[h % FALLBACK_EMOJI.length] };
}

export const productEmoji = (p) => productStyle(p).emoji;
export const productChip = (p) => productStyle(p).chip;
// The human-friendly label admins edit; falls back to the code if no label set.
export const productLabel = (p) => (p && typeof p === "object" ? (p.name && String(p.name).trim()) || p.code : p) || "";

// ─── Pool / line colours + emoji ────────────────────────────────────────────
export const POOL_COLORS = {
  "Trimming": "#d946ef", "Deboning": "#10b981", "Drumsticks": "#8b5cf6", "FOT": "#f43f5e",
  "FOM": "#0ea5e9", "FOD": "#8b5cf6", "Wings": "#06b6d4", "Pooraka M/T": "#f59e0b",
  "Pooraka Deboning": "#14b8a6", "Pool 13": "#6366f1", "Pool 14": "#64748b",
};
export const lineColor = (line) => POOL_COLORS[line] || "#0d9488";
export const lineTint = (line) => { const c = lineColor(line); return { color: c, backgroundColor: c + "1A", borderColor: c + "40" }; };
const POOL_EMOJI = {
  "Trimming": "✂️", "Deboning": "👐", "Drumsticks": "🥁", "FOT": "🍖", "FOM": "🦴",
  "FOD": "🥁", "Wings": "🪽", "Pooraka M/T": "✂️", "Pooraka Deboning": "👐", "Pool 13": "🍗", "Pool 14": "🍗",
};
export const lineEmoji = (line) => POOL_EMOJI[line] || "🥩";

// ─── Shift labels (single source of truth across app + exports) ──────────────
export const SHIFT_LABEL = {
  night: "1st Shift (Night Shift)",
  day: "2nd Shift (Day Shift)",
  afternoon: "3rd Shift (Afternoon Shift)",
  pooraka_day: "Pooraka Day Shift",
};
export const SHIFT_TIME = { night: "🌙 11pm–7am", day: "☀️ 7am–3pm", afternoon: "🌆 3pm–11pm", pooraka_day: "🏭 Pooraka day" };
export const shiftLabel = (key) => SHIFT_LABEL[key] || key || "";
